import {
  useState,
  useEffect,
  useCallback,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { OpSelect } from 'new-components/DLS/OpSelect/OpSelect';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { useSelectorJs } from 'utils/customHooks';
import { useConfirmModal } from 'utils/customHooks/useConfirmModal';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import { getOpParams } from 'utils/getOpParams';
import { heliumReceive, heliumSend } from 'utils/helpers';
import { OnSubmitArgs, OpForm } from 'new-components/DLS/OpForm/OpForm';
import { OpDivider } from 'new-components/DLS/OpDivider/OpDivider';
import { OpModal } from 'new-components/DLS/OpModal/OpModal';
import { OpSwitch } from 'new-components/DLS/OpSwitch/OpSwitch';
import { OpCheckbox } from 'new-components/DLS/OpCheckbox/OpCheckbox';

import { formatFullName } from '../../../../utils/utils';
import {
  getStatusNameById,
  getStatusColor,
  profileIcon,
} from '../../../../utils/visitorsHelper';
import STATUS from '../../../../constants/status';
import { DATE_TIME_FORMAT } from '../../../../constants/dates';
import {
  SIGNED_IN_VISITOR_ID,
  SIGNED_OUT_VISITOR_ID,
} from '../../../../constants/userActivities';
import { AppDispatch, ImmutableRootState } from '../../../../store/store';
import { fetchVisits, VisitsState } from '../../../../store/slices/visitsSlice';
import {
  searchVisitors,
  searchVisitorsToday,
  VisitorSearchState,
} from '../../../../store/slices/visitorSearchSlice';
import { fetchRedFlags } from '../../../../store/slices/redFlagSlice';
import { Visitor } from '../../../../types/visitTypes';
import { DenyReason } from '../../../../types/denyTypes';
import { VisitorWorkflowFields } from '../../../../types/visitorWorkflowTypes';
import { FormFieldWorkflow, VisitData } from '../../../../types/formFieldTypes';
import {
  CustomFieldsWorkflowResponse,
  ExcludeHostsResponse,
  HostUser,
  LocationWorkflowResponse,
  UsersResponse,
  VisitDataResponse,
  VisitorTypeWorkflowResponse,
} from '../../../../types/visitorsDrawerTypes';

import RequiredFieldsModal from './RequiredFieldsModal';
import ReturnItemsModal from './ReturnItemsModal';

interface DenyReasonsResponse {
  data?: DenyReason[] | null;
}

interface WorkflowFieldsResponse {
  data?: VisitorWorkflowFields[] | null;
}

interface WorkflowConfigResponse {
  data?: Array<{ confirmReturnItems?: number }> | null;
}

interface VisitorsModalProps {
  open: boolean;
  newStatus: number;
  onClose: () => void;
  setLoading: (loading: boolean) => void;
}

const AccessControlModal = ({
  open,
  newStatus,
  onClose,
  setLoading,
}: VisitorsModalProps) => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();
  const confirmModal = useConfirmModal();

  const [form] = OpForm.useForm();

  const { orgId } = getOpParams();
  const { userId: globalUserId } = useIdentityAndUser();
  const { isValid: hasVisitorlogWrite } = useValidateScopes([
    'o{orgId}-visitorVisitorLog:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);
  const { isValid: hasRedFlagWrite } = useValidateScopes([
    'o{orgId}-visitorRedFlag:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);

  const globalLocationId = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'globalLocation', 'id']),
  ) as number;
  const selectedVisit = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisit']),
  ) as VisitsState['selectedVisit'];
  const lastSearchParams = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorSearch', 'lastSearchParams']),
  ) as VisitorSearchState['lastSearchParams'];

  const [hosts, setHosts] = useState<HostUser[]>([]);
  const [denyReasons, setDenyReasons] = useState<DenyReason[]>([]);
  const [requiredFieldsModalOpen, setRequiredFieldsModalOpen] = useState(false);
  const [requiredFields, setRequiredFields] = useState<string[]>([]);
  const [checkedVisitors, setCheckedVisitors] = useState<Visitor[]>([]);
  const [signInWorkflowFields, setSignInWorkflowFields] =
    useState<VisitorWorkflowFields>();
  const [customFieldsWorkflow, setCustomFieldsWorkflow] = useState<
    FormFieldWorkflow[]
  >([]);

  const [returnItemsModalOpen, setReturnItemsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [accessControlVisible, setAccessControlVisible] = useState(true);

  const initialValues = { reasonForDenial: denyReasons[0]?.id };

  useEffect(() => {
    const fetchData = async () => {
      const denyReasonPriority = [
        t('visitors.accessControlModal.denyReasons.insufficientId'),
        t('visitors.accessControlModal.denyReasons.blockWatchList'),
        t('visitors.accessControlModal.denyReasons.byHostTeam'),
        t('visitors.accessControlModal.denyReasons.appointmentIssues'),
        t('visitors.accessControlModal.denyReasons.behaviorIssues'),
        t('visitors.accessControlModal.denyReasons.safetyConcerns'),
        t('visitors.accessControlModal.denyReasons.other'),
      ];
      try {
        const denyReasonsResponse = (await heliumReceive('listAllDenyReason', [
          orgId,
        ])) as DenyReasonsResponse;
        const orderedDenyReasons = (denyReasonsResponse.data ?? []).sort(
          (a: DenyReason, b: DenyReason) =>
            denyReasonPriority.indexOf(a.name ?? '') -
            denyReasonPriority.indexOf(b.name ?? ''),
        );
        setDenyReasons(orderedDenyReasons);

        const locationDefaultWorkflow = (await heliumReceive('locationConfig', [
          orgId,
          selectedVisit?.location?.id,
        ])) as LocationWorkflowResponse;
        const locationVisitorTypeWorkflow = (await heliumReceive(
          'describeVisitorTypeWorkflow',
          [orgId, selectedVisit?.location.id, selectedVisit?.visitorType.id],
        )) as VisitorTypeWorkflowResponse;

        const defaultWorkflowData = locationDefaultWorkflow?.data?.[0];
        const visitorTypeWorkflowData =
          locationVisitorTypeWorkflow?.data?.[0]?.workflow;
        const visitorTypeSignInWorkflowId =
          visitorTypeWorkflowData?.SignInWorkflowId;
        const signInWorkflowId =
          visitorTypeSignInWorkflowId !== undefined &&
          visitorTypeSignInWorkflowId !== -1
            ? visitorTypeSignInWorkflowId
            : defaultWorkflowData?.SignInWorkflowId;

        const signInWorkflowConfigResponse = (await heliumReceive(
          'getVisitorWorkflowFields',
          [orgId, signInWorkflowId],
        )) as WorkflowFieldsResponse;
        const customFieldsWorkflowResponse = (await heliumReceive(
          'getVisitorWorkflowFieldsCustom',
          [orgId, signInWorkflowId],
        )) as CustomFieldsWorkflowResponse;

        setSignInWorkflowFields(signInWorkflowConfigResponse.data?.[0]);
        setCustomFieldsWorkflow(customFieldsWorkflowResponse.data ?? []);

        const [usersRes, excludedHostsRes] = (await Promise.all([
          heliumReceive('listUsers', [orgId]),
          heliumReceive('listAllExcludeHosts', [orgId]),
        ])) as [UsersResponse, ExcludeHostsResponse];

        const activeUsers = (usersRes.data ?? []).filter(
          (user) => user.status === 'A',
        );
        const excludedIds = (excludedHostsRes.data ?? []).map(
          (ex) => ex.userId,
        );

        const filteredHosts = activeUsers.filter(
          (host) =>
            !excludedIds.includes(host.id) ||
            host.id === selectedVisit?.host?.userId,
        );
        setHosts(filteredHosts);
      } catch {
        // Failed to fetch deny reasons
      }
    };
    fetchData();
  }, [
    orgId,
    dispatch,
    selectedVisit?.location.id,
    selectedVisit?.visitorType.id,
    t,
    selectedVisit?.host?.userId,
  ]);

  const getFilteredVisitors = () => {
    return selectedVisit?.visitors.filter((visitor) => {
      if (
        newStatus === Number(STATUS.SIGNED_IN.id) ||
        newStatus === Number(STATUS.DENIED_ENTRY.id)
      ) {
        return visitor.status === STATUS.PENDING.id;
      }
      if (newStatus === Number(STATUS.SIGNED_OUT.id)) {
        return visitor.status === STATUS.SIGNED_IN.id;
      }
      return true;
    });
  };

  const visitorOptions = getFilteredVisitors()?.map((visitor) => ({
    label: formatFullName(
      visitor.firstName,
      visitor.middleName,
      visitor.lastName,
    ),
    value: `${visitor.id}`,
    firstName: visitor.firstName,
    lastName: visitor.lastName,
    color: getStatusColor(
      getStatusNameById(
        visitor.status,
        selectedVisit?.scheduleStart!,
        selectedVisit?.location?.timezone,
      ),
    ),
  }));

  const updateVisitStatus = useCallback(async () => {
    if (newStatus === Number(STATUS.SIGNED_IN.id)) {
      await heliumSend('updateVisit', [orgId, selectedVisit?.id], {
        status: newStatus,
        signIn: dayjs().utc().format(DATE_TIME_FORMAT),
      });
      notificationService.show(
        'success',
        t('visitors.accessControlModal.notification.signedInSuccess'),
      );
    } else if (newStatus === Number(STATUS.SIGNED_OUT.id)) {
      await heliumSend('newVisitorExit', [orgId, selectedVisit?.id], {
        signOut: dayjs().utc().format(DATE_TIME_FORMAT),
      });
      notificationService.show(
        'success',
        t('visitors.accessControlModal.notification.SignedOutSuccess'),
      );
    } else if (newStatus === Number(STATUS.DENIED_ENTRY.id)) {
      await heliumSend('updateVisit', [orgId, selectedVisit?.id], {
        status: newStatus,
        signIn: dayjs().utc().format(DATE_TIME_FORMAT),
      });
      notificationService.show(
        'success',
        t('visitors.accessControlModal.notification.deniedEntrySuccess'),
      );

      dispatch(fetchRedFlags({ orgId }));
    }
    onClose();
  }, [dispatch, orgId, selectedVisit, newStatus, onClose, t]);

  const updateVisitorStatus = useCallback(
    async (visitorId: number) => {
      try {
        await heliumSend(
          'updateVisitors',
          [orgId, selectedVisit?.id, visitorId],
          {
            status: newStatus,
            signIn:
              newStatus === Number(STATUS.SIGNED_IN.id) ||
              newStatus === Number(STATUS.DENIED_ENTRY.id)
                ? dayjs().utc().format(DATE_TIME_FORMAT)
                : undefined,
            signOut:
              newStatus === Number(STATUS.SIGNED_OUT.id)
                ? dayjs().utc().format(DATE_TIME_FORMAT)
                : undefined,
          },
        );
      } catch {
        // Failed to update visitor status
      }
    },
    [orgId, selectedVisit, newStatus],
  );

  const handleSubmit = useCallback(
    async ({ values }: OnSubmitArgs) => {
      setSubmitting(true);
      setLoading(true);
      const visitors = selectedVisit?.visitors.filter((visitor) =>
        values.visitorsCheckbox.includes(visitor.id.toString()),
      );

      if (newStatus === Number(STATUS.SIGNED_IN.id)) {
        const visitData = (await heliumReceive('getVisitorData', [
          orgId,
          selectedVisit?.id,
        ])) as VisitDataResponse;

        const signInRequiredFields = signInWorkflowFields
          ? Object.entries(signInWorkflowFields)
              .filter(([_, field]) => field.required === 1)
              .map(([key]) => key)
          : [];

        // Filter fields without values
        const fields = customFieldsWorkflow.filter(
          (formField: FormFieldWorkflow) => {
            const matchingVisitData = (visitData.data ?? []).find(
              (data: VisitData) =>
                data.formField.id === formField.id &&
                data.value !== null &&
                data.value !== '',
            );
            return formField.required === 1 && !matchingVisitData; // Keep only required fields without values
          },
        );

        const isMissingRequiredFields =
          (signInRequiredFields.includes('visitCompany') &&
            !selectedVisit?.company) ||
          (signInRequiredFields.includes('host') &&
            !selectedVisit?.host.userId &&
            hosts.length > 0) ||
          (signInRequiredFields.includes('visitPurpose') &&
            !selectedVisit?.purpose) ||
          visitors?.some(
            (visitor) =>
              (signInRequiredFields.includes('middleName') &&
                !visitor.middleName) ||
              (signInRequiredFields.includes('email') && !visitor.email) ||
              (signInRequiredFields.includes('visitPhone') &&
                !visitor.mobilePhone),
          ) ||
          fields.length > 0;

        if (isMissingRequiredFields) {
          setRequiredFields(signInRequiredFields);
          if (visitors) {
            setCheckedVisitors(visitors);
          }
          form.resetFields();
          setSubmitting(false);
          setAccessControlVisible(false);
          setRequiredFieldsModalOpen(true);
          return;
        }
      } else if (newStatus === Number(STATUS.SIGNED_OUT.id)) {
        const locationDefaultWorkflowSignOut = (await heliumReceive(
          'locationConfig',
          [orgId, selectedVisit?.location?.id],
        )) as LocationWorkflowResponse;
        const locationVisitorTypeWorkflowSignOut = (await heliumReceive(
          'describeVisitorTypeWorkflow',
          [orgId, selectedVisit?.location.id, selectedVisit?.visitorType.id],
        )) as VisitorTypeWorkflowResponse;

        const defaultWorkflowDataSignOut =
          locationDefaultWorkflowSignOut?.data?.[0];
        const visitorTypeWorkflowDataSignOut =
          locationVisitorTypeWorkflowSignOut?.data?.[0]?.workflow;
        const visitorTypeSignOutWorkflowId =
          visitorTypeWorkflowDataSignOut?.SignOutWorkflowId;
        const SignOutWorkflowId =
          visitorTypeSignOutWorkflowId !== undefined &&
          visitorTypeSignOutWorkflowId !== -1
            ? visitorTypeSignOutWorkflowId
            : defaultWorkflowDataSignOut?.SignOutWorkflowId;

        const signOutWorkflowConfigResponse = (await heliumReceive(
          'getVisitorWorkflowConfig',
          [orgId, SignOutWorkflowId],
        )) as WorkflowConfigResponse;

        if (signOutWorkflowConfigResponse.data?.[0]?.confirmReturnItems === 1) {
          if (visitors) {
            setCheckedVisitors(visitors);
          }
          form.resetFields();
          setSubmitting(false);
          setAccessControlVisible(false);
          setReturnItemsModalOpen(true);
          return;
        }
      }

      try {
        // Deny Entry
        if (newStatus === Number(STATUS.DENIED_ENTRY.id)) {
          try {
            const denyRequests = values.visitorsCheckbox.map(
              (visitorId: string) =>
                heliumSend('denyEntry', [orgId, selectedVisit?.id], {
                  visitorId: Number(visitorId),
                  deniedBy: Number(globalUserId),
                  reasonId: Number(values.reasonForDenial),
                  details: values.detail,
                }),
            );
            await Promise.all(denyRequests);

            // Create Red Flag API Call
            if (values.addToBlockList || values.addToWatchList) {
              await heliumSend('newRedFlag', [orgId], {
                level: values.addToBlockList ? '1' : '2',
                effectiveDate: dayjs().utc().format(DATE_TIME_FORMAT),
                visitors: values.visitorsCheckbox.map((visitorId: number) => {
                  const visitor = selectedVisit?.visitors.find(
                    (v) => Number(v.id) === Number(visitorId),
                  );
                  return {
                    firstName: visitor?.firstName,
                    lastName: visitor?.lastName,
                    middleName: visitor?.middleName,
                  };
                }),
              });

              // Dispatch custom event to notify any listening components
              window.dispatchEvent(
                new CustomEvent('visitorRedFlagAdded', {
                  detail: { orgId, level: values.addToBlockList ? 1 : 2 },
                }),
              );
            }

            const uncheckedVisitors = selectedVisit?.visitors.filter(
              (visitor) =>
                !values.visitorsCheckbox.includes(visitor.id.toString()),
            );

            const allUncheckedDenied = uncheckedVisitors?.every(
              (visitor) => visitor.status === STATUS.DENIED_ENTRY.id,
            );

            if (allUncheckedDenied) {
              await heliumSend(
                'revokeMsiAltaGuestPass',
                [orgId, selectedVisit?.id],
                {},
              );
            }
          } catch {
            // Failed in denyEntry or createRedFlag
          }
        }

        // update status of visit and visitors
        if (visitors) {
          const statusUpdateRequests = visitors
            .filter((visitor) => visitor.id)
            .map((visitor) => updateVisitorStatus(visitor.id));

          await Promise.all(statusUpdateRequests);
        }

        await updateVisitStatus();

        // log activity
        const fullNameList = visitors
          ?.map((visitor: Visitor) =>
            formatFullName(
              visitor.firstName,
              visitor.middleName,
              visitor.lastName,
            ),
          )
          .join(', ');
        if (!globalUserId) {
          notificationService.show(
            'warning',
            t('notification.activityLogMasterModeWarning'),
          );
        } else if (newStatus === Number(STATUS.SIGNED_IN.id)) {
          await heliumSend('newActivityLog', [orgId], {
            userId: globalUserId,
            activityId: SIGNED_IN_VISITOR_ID,
            details: fullNameList,
          });
        } else if (newStatus === Number(STATUS.SIGNED_OUT.id)) {
          await heliumSend('newActivityLog', [orgId], {
            userId: globalUserId,
            activityId: SIGNED_OUT_VISITOR_ID,
            details: fullNameList,
          });
        }
        dispatch(fetchVisits({ orgId }));
        if (Object.keys(lastSearchParams).length !== 0) {
          dispatch(
            searchVisitors({
              ...lastSearchParams,
              orgId,
              locationId: lastSearchParams.locationId || globalLocationId!,
            }),
          );
        }

        dispatch(searchVisitorsToday({ orgId }));

        form.resetFields();
      } catch {
        // Submission failed
      } finally {
        setSubmitting(false);
        setLoading(false);
      }
    },
    [
      orgId,
      globalUserId,
      form,
      selectedVisit,
      newStatus,
      dispatch,
      updateVisitStatus,
      updateVisitorStatus,
      lastSearchParams,
      globalLocationId,
      signInWorkflowFields,
      customFieldsWorkflow,
      hosts.length,
      setLoading,
      t,
    ],
  );

  const onOkHandler = async () => {
    const selectedVisitors = form.getFieldValue('visitorsCheckbox') || [];
    if (selectedVisitors.length === 0) {
      notificationService.show(
        'warning',
        t(
          'visitors.accessControlModal.notification.noVisitorsSelected.description',
        ),
        t('visitors.accessControlModal.notification.noVisitorsSelected.title'),
      );
      return;
    }
    form.submit();
  };

  const getTitle = () => {
    switch (newStatus) {
      case Number(STATUS.SIGNED_IN.id):
        return t('visitors.accessControlModal.title.signIn');
      case Number(STATUS.SIGNED_OUT.id):
        return t('visitors.accessControlModal.title.signOut');
      case Number(STATUS.DENIED_ENTRY.id):
        return t('visitors.accessControlModal.title.denyEntry');
      default:
        return '';
    }
  };

  const handleBlockListChange = (checked: boolean) => {
    if (checked) {
      form.setFieldsValue({ addToWatchList: false });
    }
  };

  const handleWatchListChange = (checked: boolean) => {
    if (checked) {
      form.setFieldsValue({ addToBlockList: false });
    }
  };

  const finalOnCancel = (_e: MouseEvent | KeyboardEvent) => {
    if (form.isFieldsTouched()) {
      confirmModal({
        title: t('confirmDiscard.title'),
        content: t('confirmDiscard.message'),
        okText: t('confirmDiscard.ok'),
        cancelText: t('confirmDiscard.cancel'),
        onOk: () => {
          form.resetFields();
          onClose();
        },
      });
    } else {
      onClose();
    }
  };

  return (
    <>
      <OpModal
        title={getTitle()}
        open={open && accessControlVisible}
        onOk={onOkHandler}
        onCancel={finalOnCancel}
        centered
        width={720}
        confirmLoading={submitting}
      >
        <OpDivider />

        <OpForm
          form={form}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          hasError={false}
          defaultButtons={false}
        >
          <OpForm.Item name="visitorsCheckbox">
            <OpCheckbox.Group style={{ width: '100%' }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                {visitorOptions?.map((option) => (
                  <OpCheckbox
                    key={option.value}
                    value={option.value}
                    style={{ marginBottom: '24px' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {profileIcon({
                        firstName: option.firstName || '',
                        lastName: option.lastName || '',
                        size: 28,
                        color: option.color,
                      })}
                      <span style={{ marginLeft: '12px' }}>
                        {' '}
                        {option.label}
                      </span>
                    </div>
                  </OpCheckbox>
                ))}
              </div>
            </OpCheckbox.Group>
          </OpForm.Item>

          {newStatus === Number(STATUS.DENIED_ENTRY.id) && (
            <>
              <OpForm.Item
                label={t('visitors.accessControlModal.reasonForDenial')}
                name="reasonForDenial"
              >
                <OpSelect
                  options={denyReasons.map((reason) => ({
                    label: t(reason.languageCode ?? '', reason.name ?? ''),
                    value: reason.id,
                  }))}
                />
              </OpForm.Item>
              {hasVisitorlogWrite && (
                <OpForm.TextAreaInput
                  label={t('visitors.accessControlModal.details')}
                  name="detail"
                  rows={4}
                />
              )}

              {hasRedFlagWrite && (
                <>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      marginBottom: '24px',
                    }}
                  >
                    <OpForm.Item
                      name="addToBlockList"
                      valuePropName="checked"
                      noStyle
                    >
                      <OpSwitch onChange={handleBlockListChange} />
                    </OpForm.Item>
                    <span style={{ marginLeft: 8 }}>
                      {t('visitors.accessControlModal.addToBlockList')}
                    </span>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <OpForm.Item
                      name="addToWatchList"
                      valuePropName="checked"
                      noStyle
                    >
                      <OpSwitch onChange={handleWatchListChange} />
                    </OpForm.Item>
                    <span style={{ marginLeft: 8 }}>
                      {t('visitors.accessControlModal.addToWatchList')}
                    </span>
                  </div>
                </>
              )}
            </>
          )}
        </OpForm>
      </OpModal>
      {requiredFieldsModalOpen && (
        <RequiredFieldsModal
          open={requiredFieldsModalOpen}
          requiredFields={requiredFields}
          checkedVisitors={checkedVisitors}
          onClose={() => {
            setRequiredFieldsModalOpen(false);
            setAccessControlVisible(true);
            setLoading(false);
            onClose();
          }}
          updateVisitStatus={updateVisitStatus}
          updateVisitorStatus={updateVisitorStatus}
          setLoading={setLoading}
        />
      )}
      {returnItemsModalOpen && (
        <ReturnItemsModal
          open={returnItemsModalOpen}
          checkedVisitors={checkedVisitors}
          onClose={() => {
            setReturnItemsModalOpen(false);
            setAccessControlVisible(true);
            setLoading(false);
            onClose();
          }}
          updateVisitorStatus={updateVisitorStatus}
          setLoading={setLoading}
        />
      )}
    </>
  );
};

export default AccessControlModal;
